/**
 * Camera settings: follow style (Never / Smart / Always) and the last zoom distance. Persisted
 * in localStorage separately from graphics settings; every storage access is in try/catch.
 */
import { CAMERA_FOLLOW_MODES, CAMERA_LIMITS, type CameraFollowMode, type CameraRig, DEFAULT_DISTANCE } from './camera';

export interface CameraSettings {
  /** Camera returns behind the character: never (default, camera-relative movement), smart (WoW), always. */
  follow: CameraFollowMode;
  /** Last settled zoom distance (yards). */
  zoom: number;
}

export const DEFAULT_CAMERA_SETTINGS: Readonly<CameraSettings> = Object.freeze({ follow: 'never', zoom: DEFAULT_DISTANCE });
export const CAMERA_SETTINGS_KEY = 'keystone.camera.v1';

export function sanitizeCameraSettings(raw: unknown): CameraSettings {
  const s: CameraSettings = { ...DEFAULT_CAMERA_SETTINGS };
  if (!raw || typeof raw !== 'object') return s;
  const r = raw as Record<string, unknown>;
  if (typeof r['follow'] === 'string' && (CAMERA_FOLLOW_MODES as readonly string[]).includes(r['follow'])) s.follow = r['follow'] as CameraFollowMode;
  if (typeof r['zoom'] === 'number' && Number.isFinite(r['zoom'])) s.zoom = Math.min(CAMERA_LIMITS.maxDist, Math.max(CAMERA_LIMITS.minDist, r['zoom']));
  return s;
}

function defaultStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function loadCameraSettings(storage: Pick<Storage, 'getItem'> | null = defaultStorage()): CameraSettings {
  try {
    const raw = storage?.getItem(CAMERA_SETTINGS_KEY);
    return sanitizeCameraSettings(raw ? JSON.parse(raw) : null);
  } catch {
    return { ...DEFAULT_CAMERA_SETTINGS };
  }
}

export function saveCameraSettings(s: CameraSettings, storage: Pick<Storage, 'setItem'> | null = defaultStorage()): boolean {
  try {
    if (!storage) return false;
    storage.setItem(CAMERA_SETTINGS_KEY, JSON.stringify(sanitizeCameraSettings(s)));
    return true;
  } catch {
    return false;
  }
}

type Listener = (s: CameraSettings) => void;

class CameraSettingsStore {
  private value: CameraSettings | null = null;
  private readonly listeners = new Set<Listener>();

  get(): CameraSettings {
    return (this.value ??= loadCameraSettings());
  }

  set(patch: Partial<CameraSettings>): void {
    this.value = sanitizeCameraSettings({ ...this.get(), ...patch });
    saveCameraSettings(this.value);
    for (const l of this.listeners) l(this.value);
  }

  /** Calls `fn` now and on every change. Returns an unsubscribe function. */
  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    fn(this.get());
    return () => this.listeners.delete(fn);
  }
}

export const cameraSettings = new CameraSettingsStore();

/** Restore the saved zoom into a rig, keep its follow mode in sync and save zoom changes. Returns an unbind. */
export function bindCameraSettings(rig: CameraRig): () => void {
  rig.setDistance(cameraSettings.get().zoom);
  // Zoom saves don't notify the follow subscription into a loop (it only reads `follow`).
  rig.onZoomSettled = (d) => {
    if (Math.abs(d - cameraSettings.get().zoom) > 0.05) cameraSettings.set({ zoom: Math.round(d * 100) / 100 });
  };
  const off = cameraSettings.subscribe((s) => {
    rig.followMode = s.follow;
  });
  return () => {
    off();
    rig.onZoomSettled = null;
  };
}
